import { track } from "@/features/analytics";
import { useGetSearchTermMutation } from "@api/ai";
import { useSearchVideosMutation } from "@api/media";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  VIDEOS_PANEL_STOCK_CATEGORIES,
  VIDEOS_PANEL_STOCK_QUERY_KEYS,
} from "../constants";
import { useStockStoreShallow } from "../store";
import { prepareVideosPanelAiPrompt } from "../utils";

const PER_PAGE = 30;

interface IProps {
  searchQuery: string;
}

interface IFetchProps {
  replace?: boolean;
  signal?: AbortSignal;
  nextPage?: boolean;
  query?: string;
}

export const useStockFetchVideos = ({ searchQuery }: IProps) => {
  const { category, columnsData, setColumnsData } = useStockStoreShallow(
    (s) => ({
      columnsData: s.columnsData,
      setColumnsData: s.setColumnsData,
      category: s.category,
    }),
  );

  const isInit = useRef(true);
  const [isReplacingVideos, setIsReplacingVideos] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isError, setIsError] = useState(false);
  const [reloadKey, setReloadKey] = useState({});
  const latestPage = useRef(0);
  const lastSystemQuery = useRef("");

  const [fetchVideos] = useSearchVideosMutation({
    fixedCacheKey: VIDEOS_PANEL_STOCK_QUERY_KEYS.VIDEOS,
  });
  const [getSearchTerm] = useGetSearchTermMutation();

  const fetchPageOfVideos = async ({
    query = searchQuery || lastSystemQuery.current,
    signal,
    replace,
    nextPage,
  }: IFetchProps) => {
    setIsError(false);
    setIsFetchingMore(true);
    try {
      if (replace) {
        setIsReplacingVideos(true);
        latestPage.current = 0;
      } else if (nextPage) {
        setIsFetchingMore(true);
        latestPage.current++;
      }

      const data = await fetchVideos({
        page: latestPage.current,
        per_page: PER_PAGE,
        query: query,
        signal: signal,
      }).unwrap();

      setColumnsData({
        videos: data.videos,
        extend: nextPage,
      });
    } catch (e) {
      console.log(e);
      track("error fetching pexel videos", {
        error: e,
        query: query,
      });
      setIsError(true);
    } finally {
      setIsReplacingVideos(false);
      setIsFetchingMore(false);
    }
  };

  const replaceVideosData = async (controller: AbortController) => {
    setIsError(false);
    setIsReplacingVideos(true);

    let query = searchQuery;
    if (query.length === 0) {
      const found = VIDEOS_PANEL_STOCK_CATEGORIES.find(
        (c) => c.key === category,
      );
      if (found) {
        if (found.key === "ai_suggestions") {
          const prompt = prepareVideosPanelAiPrompt();
          query = VIDEOS_PANEL_STOCK_CATEGORIES[1].query;
          if (prompt) {
            try {
              const answer = (await getSearchTerm({
                prompt,
                signal: controller.signal,
                type: "json_object",
              }).unwrap()) as { search?: string };

              if (
                typeof answer === "object" &&
                Object.hasOwn(answer, "search") &&
                typeof answer.search === "string"
              ) {
                query = answer.search;
              }
            } catch (e) {
              console.log(e);
              track("error generating ai search terms", {
                error: e,
                prompt,
              });
            }
          }
        } else {
          query = found.query;
        }
      }
    }
    if (controller.signal.aborted) {
      setIsReplacingVideos(false);
      return;
    }

    lastSystemQuery.current = query;
    await fetchPageOfVideos({
      query,
      replace: true,
      signal: controller.signal,
    });
    setIsReplacingVideos(false);
  };

  useEffect(() => {
    const controller = new AbortController();

    debounce(async () => {
      if (isInit.current) {
        isInit.current = false;
        if (columnsData[0].length === 0) {
          replaceVideosData(controller);
        } else {
          setIsReplacingVideos(false);
        }
        return;
      }

      replaceVideosData(controller);
    }, 100)();

    return () => {
      controller.abort();
    };
  }, [searchQuery, category, reloadKey]);

  const canLoadMoreVideos = !isFetchingMore && !isReplacingVideos;

  const fetchMoreVideos = useCallback(
    debounce(() => {
      if (
        !canLoadMoreVideos ||
        columnsData.reduce((sum, col) => sum + col.length, 0) < PER_PAGE / 2
      ) {
        return;
      }
      fetchPageOfVideos({ nextPage: true });
    }, 100),
    [canLoadMoreVideos, columnsData, fetchPageOfVideos],
  );

  return {
    isError,
    reloadData: () => setReloadKey({}),
    fetchMoreVideos,
    fetchPageOfVideos,
    shouldShowSkeleton: isReplacingVideos,
    canLoadMoreVideos,
  };
};
